import { FirebaseAuthTypes, signOut as signOutMobile } from '@react-native-firebase/auth';
import { useQueryClient } from '@tanstack/react-query';
import { Image } from 'expo-image';
import { router, useLocalSearchParams, useNavigation } from 'expo-router';
import { Auth, signInWithCustomToken as signInWithCustomTokenWeb, signOut as signOutWeb } from 'firebase/auth';
import { getIdToken as getIdTokenWeb, User } from 'firebase/auth';
import { useEffect, useLayoutEffect, useState } from 'react';
import { Linking, Platform, StyleSheet, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { client } from '@/api';
import { useValidateRedirect } from '@/api/hooks/authQueryHooks';
import { useGetBillingInfo } from '@/api/hooks/individualHooks';
import { cardIcon, npoIcon } from '@/assets/images/icons';
import GoBackButton from '@/components/buttons/GoBackButton';
import GradientCircularButton from '@/components/buttons/GradientCircularButton';
import Card from '@/components/Card';
import DotsLoader from '@/components/DotsLoader';
// import ApplePayButton from '@/components/paymentMethods/AppleyPayButton';
import Text from '@/components/Text';
import { APPSFLYER_CONFIG } from '@/constants/config';
import { useAuth } from '@/contexts/authContext';
import { useMeFavorites } from '@/contexts/favoritesContext';
import { auth } from '@/firebaseConfig';
import { decryptObject, encryptObject } from '@/utils/crypto';
import { i18n } from '@/utils/i18n';

export enum PaymentMethod {
  CARD = 'card',
  ACH = 'ach'
}

const SelectPaymentMethod = () => {
  const navigation = useNavigation();

  // Only send encrypted payload
  const { payload } = useLocalSearchParams<{
    payload: string;
  }>();

  const { state, dispatch, clearCurrentUser } = useAuth();
  const { clearFavorites } = useMeFavorites();

  const queryClient = useQueryClient();
  const { data: individualNuveiUser, isFetching } = useGetBillingInfo();
  const { mutateAsync: validateRedirect, isPending: isValidatingRedirect } = useValidateRedirect();

  const [payloadState, setPayloadState] = useState<{
    donationData: string;
    availableMethods: string;
    oneTimeToken?: string;
    fromIOS?: string;
    influencerCode?: string;
    routeWithSelectedMethod?: string;
    subscriptionItem?: string;
    showId?: string;
    showTitle?: string;
    showPrice?: string;
  }>();

  const [authenticating, setAuthenticating] = useState(payloadState?.oneTimeToken && Platform.OS === 'web');

  useEffect(() => {
    const loadPayloadParams = async () => {
      const payloadObject = await decryptObject<{
        donationData: string;
        availableMethods: string;
        oneTimeToken?: string;
        fromIOS?: string;
        influencerCode?: string;
        routeWithSelectedMethod?: string;
        subscriptionItem?: string;
        showId?: string;
        showTitle?: string;
        showPrice?: string;
      }>(decodeURIComponent(payload));

      if (payloadObject) {
        setPayloadState(payloadObject);

        if (isValidatingRedirect) return;
        if (payloadObject.oneTimeToken && payloadObject.fromIOS && Platform.OS === 'web' && state.authReady) {
          setAuthenticating(true);
          try {
            validateRedirect(
              { oneTimeToken: payloadObject.oneTimeToken },
              {
                onSuccess: (data) => {
                  signInWithCustomTokenWeb(auth() as Auth, data.customFbJwt).then(
                    // User logged in
                    (userCredential) => {
                      const tokenPromise = getIdTokenWeb(userCredential.user as User, true);
                      // Force refresh of token to update token claims of firebase
                      tokenPromise.then((refreshedToken) => {
                        client.defaults.headers.common['Authorization'] = `Bearer ${refreshedToken}`;
                        // Force refresh of get billing info query
                        queryClient.invalidateQueries({ queryKey: ['get-billing-info'] });
                        setAuthenticating(false);
                      });
                    },
                    // User couldn't log
                    (e) => {
                      console.error(e);
                      const params = new URLSearchParams({
                        deep_link_value: payloadState?.showId ? 'purchaseShow' : 'donation',
                        dismissBrowser: 'true'
                      });
                      const backRoute = `${APPSFLYER_CONFIG.af_dp}?${params.toString()}`;
                      Linking.openURL(backRoute);
                    }
                  );
                }
              }
            );
          } catch (e) {
            console.error(e);
            const params = new URLSearchParams({
              deep_link_value: payloadState?.showId ? 'purchaseShow' : 'donation',
              dismissBrowser: 'true'
            });
            const backRoute = `${APPSFLYER_CONFIG.af_dp}?${params.toString()}`;
            Linking.openURL(backRoute);
          }
        }
      }
    };

    if (payload) {
      loadPayloadParams();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payload]);

  useLayoutEffect(() => {
    if (payloadState?.fromIOS) {
      navigation.setOptions({
        headerLeft: () => (
          <GoBackButton
            onPress={() => {
              dispatch({ type: 'SIGN_OUT' });
              clearCurrentUser();
              if (Platform.OS === 'web') {
                signOutWeb(auth() as Auth);
              } else {
                signOutMobile(auth() as FirebaseAuthTypes.Module);
              }
              clearFavorites();
              const params = new URLSearchParams({
                deep_link_value: payloadState?.showId ? 'purchaseShow' : 'donation',
                dismissBrowser: 'true'
              });
              const backRoute = `${APPSFLYER_CONFIG.af_dp}?${params.toString()}`;
              Linking.openURL(backRoute);
            }}
          />
        )
      });
    }
  }, [clearCurrentUser, clearFavorites, dispatch, payloadState?.fromIOS, navigation, payloadState?.showId]);

  if (authenticating || isValidatingRedirect)
    return (
      <View style={styles.loaderContainer}>
        <Text>{i18n.t('mix.securing_session')}</Text>
        <DotsLoader />
      </View>
    );

  if (isFetching)
    return (
      <View style={styles.loaderContainer}>
        <DotsLoader />
      </View>
    );

  return (
    <SafeAreaView style={styles.mainContainer} edges={['bottom']}>
      <View style={styles.container}>
        {(payloadState?.availableMethods === 'BOTH' ||
          payloadState?.availableMethods === 'CREDIT_CARD' ||
          payloadState?.availableMethods === undefined) && (
          <Card
            style={[styles.cardContent]}
            onPress={async () => {
              if (individualNuveiUser === undefined || individualNuveiUser === null) {
                const newPayload = {
                  ...payloadState,
                  individualNuveiUser: undefined,
                  paymentMethod: PaymentMethod.CARD
                };

                const encrypted = await encryptObject(newPayload);

                router.navigate({
                  pathname: '/paymentMethods/billingAddress',
                  params: {
                    payload: encodeURIComponent(encrypted)
                  }
                });
              } else {
                const newPayload = {
                  ...payloadState,
                  individualNuveiUser: JSON.stringify(individualNuveiUser)
                };

                const encrypted = await encryptObject(newPayload);

                router.navigate({
                  pathname: '/paymentMethods/creditCardList',
                  params: {
                    payload: encodeURIComponent(encrypted)
                  }
                });
              }
            }}
            activeOpacity={0.5}>
            <GradientCircularButton disabled>
              <Image source={cardIcon} style={styles.icon} contentFit="contain" />
            </GradientCircularButton>
            <View style={styles.cardTextContent}>
              <Text fontWeight="900" style={styles.cardTitle}>
                {i18n.t('payment_methods.card')}
              </Text>
            </View>
          </Card>
        )}

        {(payloadState?.availableMethods === 'BOTH' ||
          payloadState?.availableMethods === 'ACH' ||
          payloadState?.availableMethods === undefined) && (
          <Card
            style={[styles.cardContent]}
            onPress={async () => {
              if (individualNuveiUser === undefined || individualNuveiUser === null) {
                const newPayload = {
                  ...payloadState,
                  individualNuveiUser: undefined,
                  paymentMethod: PaymentMethod.ACH
                };

                const encrypted = await encryptObject(newPayload);

                router.navigate({
                  pathname: '/paymentMethods/billingAddress',
                  params: {
                    payload: encodeURIComponent(encrypted)
                  }
                });
              } else {
                const newPayload = {
                  ...payloadState,
                  individualNuveiUser: JSON.stringify(individualNuveiUser)
                };

                const encrypted = await encryptObject(newPayload);

                router.navigate({
                  pathname: '/paymentMethods/achList',
                  params: {
                    payload: encodeURIComponent(encrypted)
                  }
                });
              }
            }}
            activeOpacity={0.5}>
            <GradientCircularButton disabled>
              <Image source={npoIcon} style={styles.icon} contentFit="contain" />
            </GradientCircularButton>
            <View style={styles.cardTextContent}>
              <Text fontWeight="900" style={styles.cardTitle}>
                {i18n.t('payment_methods.ach')}
              </Text>
            </View>
          </Card>
        )}
        {/* ToDO: Uncomment this when implementing ApplePay */}
        {/* {Platform.OS === 'web' && payloadState?.showId && payloadState.showTitle && payloadState.showPrice && (
          <ApplePayButton
            paymentRequest={{
              countryCode: 'US',
              currencyCode: 'USD',
              merchantCapabilities: ['supports3DS'],
              supportedNetworks: ['visa', 'masterCard', 'amex'],
              total: {
                label: payloadState.showTitle,
                amount: payloadState.showPrice
              }
            }}
          />
        )} */}
      </View>
    </SafeAreaView>
  );
};

export default SelectPaymentMethod;

const styles = StyleSheet.create({
  mainContainer: {
    flex: 1
  },

  loaderContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    gap: 5
  },

  container: {
    paddingHorizontal: Platform.OS !== 'web' ? 18 : 0,
    flexDirection: 'column',
    flex: 1
  },

  icon: {
    height: '65%',
    width: '65%'
  },

  cardContent: {
    paddingHorizontal: 20,
    flex: 1,
    flexDirection: 'row',
    marginBottom: 25,
    paddingVertical: 10,
    maxHeight: 156
  },

  cardTextContent: {
    marginLeft: 17
  },

  cardTitle: {
    fontSize: 20,
    color: 'black'
  },

  cardDescription: {
    fontSize: 14,
    textAlign: 'center'
  },

  title: {
    fontSize: 40
  },

  text: {
    fontSize: 18
  }
});
